import { ApolloProvider } from '@apollo/client';
import { Fragment, useEffect } from 'react';
import { IntlProvider } from 'react-intl';

import { HelmetProvider } from '@xing-com/crate-helmet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';
import { RuntimeProvider } from '@xing-com/crate-xinglet/internal';

import { createApolloClient } from './create-apollo-client';

const LoadingStateHandler: React.FC<{ runtime: Runtime }> = ({ runtime }) => {
  useEffect(() => {
    function startWaiting(name: string): void {
      const eventual = runtime.cachedXinglets.get(name);
      if (!eventual || eventual.settled) return;
      if (waitingFor.add(name)) eventual.then(() => stopWaiting(name));
    }

    function stopWaiting(name: string): void {
      if (waitingFor.delete(name) && waitingFor.size === 0) {
        mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
      }
    }

    function onXingletRequested(name: string): void {
      startWaiting(name);
    }

    let counter = 0;
    const mountpoint = document.querySelector('#app[data-mountpoint]');
    const waitingFor = new Set<string>();

    for (const name of runtime.cachedXinglets.keys()) {
      startWaiting(name);
    }

    if (waitingFor.size === 0) {
      mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
    }

    runtime.events.on('xinglet-requested', onXingletRequested);

    return () => {
      runtime.events.off('xinglet-requested', onXingletRequested);
    };
  });

  return <></>;
};

export function createEntryPoint(
  runtime: Runtime,
  name: string
): React.JSX.Element {
  const { language } = runtime.config;
  const { languageData: messages = {} } = runtime;
  const { XingletLoader } = runtime.host;
  const apolloClient = createApolloClient(runtime);

  return (
    <RuntimeProvider runtime={runtime}>
      <HelmetProvider>
        <IntlProvider
          locale={language}
          messages={messages}
          textComponent={Fragment}
        >
          <ApolloProvider client={apolloClient}>
            <LoadingStateHandler runtime={runtime} />
            <XingletLoader runtime={runtime} name={name} />
          </ApolloProvider>
        </IntlProvider>
      </HelmetProvider>
    </RuntimeProvider>
  );
}
